import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "../../../api";
import formatMoney from "../../../utils/formatMoney";
import Globals from "../../../Globals";
import '../css/budgetModal.css';

const BudgetProceduresModal = ({ open, patient, onClose, onRefresh }) => {
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [procedureSuggestions, setProcedureSuggestions] = useState({});
  const [selectedPartners, setSelectedPartners] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchSuggestionsForProcedures = useCallback(async (proceduresList) => {
    if (!proceduresList || proceduresList.length === 0) return;
    
    setLoading(true);
    try {
      // Executa todas as requisições em paralelo
      const results = await Promise.all(
        proceduresList.map(proc => 
          api.post('api/suggestion/scheduling/affiliates', { procedure_id: [proc.procedure_id] })
            .then(res => ({ 
              proc, 
              suggestions: res.data.data.suggestions 
            }))
        )
      );

      // Atualiza estados em lote
      setProcedureSuggestions(prev => {
        const next = { ...prev };
        results.forEach(({ proc, suggestions }) => {
          next[proc.procedure_id] = suggestions;
        });
        return next;
      });

      setSelectedPartners(prev => {
        const next = { ...prev };
        results.forEach(({ proc, suggestions }) => {
          if (suggestions.length === 1) {
            next[proc.procedure_id] = suggestions[0].id;
          } else {
            const match = suggestions.find(s => s.id === proc.affiliate_company_id);
            if (match) {
              next[proc.procedure_id] = match.id;
            }
          }
        });
        return next;
      });

    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && patient?.pending) {
      const budgetedProcedures = patient.pending.filter(proc => proc.is_budgeted === true);
      if (budgetedProcedures.length > 0) {
        setSelectedProcedures(budgetedProcedures);
        fetchSuggestionsForProcedures(budgetedProcedures);
      }
    }
  }, [open, patient, fetchSuggestionsForProcedures]);

  const handleSelectAll = async () => {
    const allProcedures = patient.pending || [];
    const allSelected = selectedProcedures.length === patient.pending?.length;

    if (allSelected) {
      // Desselecionar todos
      setSelectedProcedures([]);
      setProcedureSuggestions({});
      setSelectedPartners({});
    } else {
      setSelectedProcedures(allProcedures);
      fetchSuggestionsForProcedures(allProcedures);
    }
  };

  const handleProcedureSelect = async (proc) => {
    const exists = selectedProcedures.some((p) => p.procedure_id === proc.procedure_id);
    const next = exists
      ? selectedProcedures.filter((p) => p.procedure_id !== proc.procedure_id)
      : [...selectedProcedures, proc];

    setSelectedProcedures(next);

    if (!exists) {
      setLoading(true);

      await api.post('api/suggestion/scheduling/affiliates', {
        procedure_id: [proc.procedure_id]
      })
        .then(response => {
          const suggestions = response.data.data.suggestions;
          setProcedureSuggestions(prev => ({
            ...prev,
            [proc.procedure_id]: suggestions
          }));

          if (suggestions.length === 1) {
            setSelectedPartners(prev => ({
              ...prev,
              [proc.procedure_id]: suggestions[0].id
            }));
          }
        })
        .finally(() => {
          setLoading(false);
        })
    } else {
      setProcedureSuggestions(prev => {
        const newSuggestions = { ...prev };
        delete newSuggestions[proc.procedure_id];
        return newSuggestions;
      });
      setSelectedPartners(prev => {
        const newPartners = { ...prev };
        delete newPartners[proc.procedure_id];
        return newPartners;
      });
    }
  };

  const handlePartnerSelect = (procedureId, partnerId) => {
    setSelectedPartners(prev => ({
      ...prev,
      [procedureId]: Number(partnerId)
    }));
  };

  const calculateTotal = () => {
    return selectedProcedures.reduce((total, proc) => {
      const partnerId = selectedPartners[proc.procedure_id];
      if (!partnerId) return total;

      const suggestions = procedureSuggestions[proc.procedure_id] || [];
      const selectedPartner = suggestions.find(s => s.id == partnerId);

      if (!selectedPartner || !selectedPartner.procedures?.[0]) return total;

      const price = Number(selectedPartner.procedures[0].selling_price) || 0;
      return total + price;
    }, 0);
  };

  const generateBudget = async (e) => {
    e.preventDefault();

    const partnerKeys = Object.keys(selectedPartners || []);

    if (!Array.isArray(selectedProcedures) || partnerKeys.length === 0) {
      return [];
    }

    Swal.fire({
      title: "Aguarde",
      text: "Confirmando orçamento...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    })

    let pdfPath;

    const matchedProcedures = selectedProcedures
      .filter(proc => partnerKeys.includes(String(proc.procedure_id)));

    await api.post(`api/cashiers/pos/budget`, {
      clinic_procedure_id: selectedProcedures[0].id,
      shopper_id: 14, // FIXO ACHEMED
      procedures: patient.pending.map(proc => ({
        procedure_id: proc.procedure_id,
        clinic_procedure_id: proc.id,
        // true if it's in selectedProcedures, false otherwise
        is_budget: selectedProcedures.some(selectedProc => selectedProc.id === proc.id),
        // value of selected partner, or null if it's not included
        affiliate_company_id: selectedPartners[proc.procedure_id] || null
      }))
    })
      .then(response => {
        pdfPath = Globals.BASE_URL + '/storage/' + response.data.path
      })
      .then(() => {
        Swal.close();

        Swal.fire({
          icon: 'success',
          title: 'Orçamento gerado com sucesso!',
          showConfirmButton: true,
          confirmButtonText: 'Abrir em uma nova guia',
          allowOutsideClick: false,
        })
          .then((result) => {
            if (result.isConfirmed) {
              window.open(pdfPath, '_blank');

              if (onRefresh) {
                onRefresh();
              }

              setSelectedProcedures([])
              setProcedureSuggestions({})
              setSelectedPartners({})
              onClose();
            }
          });

        if (onRefresh) {
          onRefresh();
        }

        onClose();
      })
      .catch(error => {
        console.error(error);

        Swal.fire({
          title: "Erro",
          text: "Houve um erro ao gerar orçamento",
          icon: "error"
        })
      })
  }

  return (
    <div className="modal-overlay">
      <div className="procedures-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>Orçamento – {patient.name}</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <h3 className="mx-auto mt-3">Selecione os procedimentos para montar o orçamento</h3>

        <div className="mb-3 px-3">
          <div
            className="procedure-item select-all-item"
            onClick={handleSelectAll}
          >
            <div className="procedure-checkbox">
              <input
                type="checkbox"
                checked={selectedProcedures.length === patient.pending?.length && patient.pending?.length > 0}
                readOnly
              />
            </div>
            <div className="procedure-details">
              <h4>Selecionar todos</h4>
            </div>
          </div>
        </div>

        <div className="procedures-list">
          {Array.isArray(patient.pending) &&
            patient.pending
              .sort((a, b) => a.id - b.id) // Ordenar por ID
              .map((proc) => {
                const selected = selectedProcedures.some((p) => p.id === proc.id);
                const hasPartnerSelected = selectedPartners[proc.procedure_id];
                const suggestions = procedureSuggestions[proc.procedure_id] || [];
                const hasSuggestions = suggestions.length > 0;

                // Determinar classe visual baseada no estado do parceiro
                let partnerStateClass = '';
                if (selected) {
                  if (hasPartnerSelected) {
                    partnerStateClass = 'partner-selected'; // 1 - parceiro selecionado
                  } else if (hasSuggestions) {
                    partnerStateClass = 'partner-pending'; // 2 - precisa selecionar parceiro
                  } else {
                    partnerStateClass = 'partner-none'; // 3 - sem sugestões
                  }
                }

                return (
                  <div
                    key={proc.id}
                    className={`procedure-item ${selected ? "selected" : ""} ${partnerStateClass}`}
                    onClick={() => handleProcedureSelect(proc)}>
                    <div className="procedure-checkbox">
                      <input type="checkbox" checked={selected} readOnly />
                    </div>

                    <div className="procedure-details">
                      <h4>{proc.name}</h4>
                      <small>
                        código: {proc.cbhpm_code} &nbsp;|&nbsp; setor:{" "}
                        {proc.setor_name}
                      </small>

                    </div>
                    {selected && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown onSelect={(partnerId) => handlePartnerSelect(proc.procedure_id, partnerId)}>
                          {
                            loading ? (
                              <Dropdown.Toggle variant="outline-secondary" className="my-3" disabled>
                                Carregando ...
                              </Dropdown.Toggle>
                            ) : (
                              <Dropdown.Toggle
                                variant={
                                  hasPartnerSelected ? "success" :
                                    hasSuggestions ? "warning" :
                                      "danger"
                                }
                                className="my-3">
                                {hasPartnerSelected
                                  ? suggestions.find(s => s.id == hasPartnerSelected)?.name || "Selecione um parceiro"
                                  : hasSuggestions
                                    ? "Selecione um parceiro"
                                    : "Sem sugestão de parceiros"}
                              </Dropdown.Toggle>
                            )
                          }

                          <Dropdown.Menu>
                            {suggestions.length === 0 ? (
                              <Dropdown.Item disabled>Sem sugestão de parceiros</Dropdown.Item>
                            ) : (
                              suggestions.map((suggestion) => (
                                <Dropdown.Item key={suggestion.id} eventKey={suggestion.id}>
                                  {suggestion.name}
                                </Dropdown.Item>
                              ))
                            )}
                          </Dropdown.Menu>
                        </Dropdown>

                        {hasPartnerSelected && (
                          <div className="procedure-price text-end">
                            <span>
                              {(() => {
                                const selectedPartner = suggestions.find(s => s.id == hasPartnerSelected);
                                return selectedPartner ? formatMoney(selectedPartner.procedures[0].selling_price) : '0.00';
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

          {/* {filteredProcedures.length === 0 && (
                  <p className="no-procedures">Nenhum procedimento encontrado</p>
                )} */}
        </div>


        {/* {currentStep === 2 && (
          <>
            <h3 className="mx-auto mt-3">Selecione um parceiro para o orçamento</h3>
            <div className="doctors-list">
              {Array.isArray(partners?.data?.suggestions) &&
                partners.data.suggestions.map((partner) => {
                  let totalPrice = 0;
                  const prices = {};
                  const proceduresMap = [];

                  partner.procedures.forEach((proc) => {
                    const selling = Number(proc.selling_price ?? 0);
                    const cost = Number(proc.cost_price ?? 0);

                    prices[proc.id] = selling;
                    if (proc.payment?.type !== "insurance") {
                      totalPrice += selling;
                    }

                    proceduresMap.push({
                      id: proc.id,
                      name: proc.name,
                      code: proc.code,
                      payment: proc.payment,
                      selling_price: selling,
                      cost_price: cost,
                    });
                  });

                  return (
                    <div
                      key={partner.id}
                      className="doctor-card"
                    >
                      <div className="doctor-avatar">
                        <Stethoscope size={24} />
                      </div>

                      <div className="doctor-info">
                        <h3>{partner.name}</h3>
                        <p className="doctor-specialty">
                          {partner.specialty || "Especialidade"}
                        </p>

                        <div className="doctor-procedures">
                          <p>Procedimentos:</p>
                          <ul>
                            {proceduresMap.map((proc) => (
                              <li key={proc.id}>
                                <CheckCircle size={14} className="check-icon" />
                                <span>
                                  {proc.name}:{" "}
                                  {proc.payment?.type === "insurance"
                                    ? "Convênio"
                                    : `R$ ${proc.selling_price.toFixed(2)}`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="doctor-price">
                        <DollarSign size={20} />
                        <div className="price-info">
                          <span className="price-label">Valor&nbsp;Total:</span>
                          <span className="price-value">
                          
                            {`R$ ${Number(totalPrice.toFixed(2))}`}
                          </span>
                        </div>
                      </div>
                    </div>
                   
                  )
                })}
            
            </div>
            
          </>
        )} */}

        <div className="my-3 d-flex justify-content-between px-5">
          <h4>
            Procedimentos: {Object.entries(selectedPartners).length}
          </h4>
          <h4>
            Total: {formatMoney(calculateTotal())}
          </h4>
        </div>

        <div className="modal-footer">
          {/* {selectedProcedures.length > 0 && (
            <div className="available-doctors">
              <Users size={16} />
              <span>
                {loadingDoctors
                  ? "Carregando sugestões..."
                  : availableDoctorsCount > 0
                    ? `${availableDoctorsCount} sugestão(ões) disponível(is)`
                    : "Nenhuma sugestão disponível"}
              </span>
            </div>
          )} */}

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="schedule-button"
              onClick={generateBudget}
              disabled={
                selectedProcedures.length === 0
                || Object.entries(selectedPartners).length === 0
                || loading
              }
            >
              Gerar orçamento
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

export default BudgetProceduresModal;